
// src/pages/CaseStudiesPage.js
import React, { useState } from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { caseStudies } from '../data/casestudies_data';

const CaseStudiesPage = () => {
  const [filters, setFilters] = useState({
    industry: '',
    technology: '',
    searchTerm: ''
  });
  
  const industries = [...new Set(caseStudies.map(cs => cs.industry))];
  const technologies = [...new Set(caseStudies.flatMap(cs => cs.technologies))];
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };
  
  const filteredCaseStudies = caseStudies.filter(cs => {
    return (
      (filters.industry === '' || cs.industry === filters.industry) &&
      (filters.technology === '' || cs.technologies.includes(filters.technology)) &&
      (
        filters.searchTerm === '' || 
        cs.title.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
        cs.description.toLowerCase().includes(filters.searchTerm.toLowerCase())
      )
    );
  });
  
  return (
    <>
      {/* Hero Section */}
      <section className="bg-primary text-white py-5">
        <Container>
          <Row className="align-items-center">
            <Col lg={8} className="mx-auto text-center">
              <h1 className="display-4 fw-bold mb-4">Case Studies</h1>
              <p className="lead">
                Explore how we've helped businesses across various industries leverage technology to solve 
                complex challenges, drive innovation, and achieve measurable results.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Case Studies Listing */}
      <section className="section-padding">
        <Container>
          <Row>
            <Col lg={3} className="mb-4">
              <Card className="border-0 shadow-sm sticky-top" style={{ top: '20px' }}>
                <Card.Body>
                  <h4 className="mb-4">Filters</h4>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Search</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search case studies..."
                      name="searchTerm"
                      value={filters.searchTerm}
                      onChange={handleFilterChange}
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Industry</Form.Label>
                    <Form.Select
                      name="industry"
                      value={filters.industry}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Industries</option>
                      {industries.map((industry, index) => (
                        <option key={index} value={industry}>{industry}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Label>Technology</Form.Label>
                    <Form.Select
                      name="technology"
                      value={filters.technology}
                      onChange={handleFilterChange}
                    >
                      <option value="">All Technologies</option>
                      {technologies.map((tech, index) => (
                        <option key={index} value={tech}>{tech}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  
                  <Button 
                    variant="outline-secondary" 
                    className="w-100"
                    onClick={() => setFilters({
                      industry: '',
                      technology: '',
                      searchTerm: ''
                    })}
                  >
                    Clear Filters
                  </Button>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={9}>
              <h2 className="mb-4">Our Success Stories ({filteredCaseStudies.length})</h2>
              
              {filteredCaseStudies.length === 0 ? (
                <div className="text-center py-5">
                  <h4>No case studies match your filters</h4>
                  <p>Try adjusting your search criteria or <Button variant="link" className="p-0" onClick={() => setFilters({
                    industry: '',
                    technology: '',
                    searchTerm: ''
                  })}>clear all filters</Button>.</p>
                </div>
              ) : (
                <Row>
                  {filteredCaseStudies.map((caseStudy, index) => (
                    <Col lg={6} className="mb-4" key={index}>
                      <Card className="card-custom h-100">
                        {/* <Card.Img variant="top" src={caseStudy.image} alt={caseStudy.title} /> */}
                        <Card.Body>
                          <div className="mb-2">
                            <span className="badge bg-primary me-2">{caseStudy.industry}</span>
                            {caseStudy.technologies.slice(0, 2).map((tech, techIndex) => (
                              <span key={techIndex} className="badge bg-secondary me-2">{tech}</span>
                            ))}
                            {caseStudy.technologies.length > 2 && (
                              <span className="badge bg-light text-dark">+{caseStudy.technologies.length - 2} more</span>
                            )}
                          </div>
                          <Card.Title as="h4" className="mb-3">{caseStudy.title}</Card.Title>
                          <Card.Text>{caseStudy.description}</Card.Text>
                          <div className="mt-3">
                            <Link to={`/case-studies/${caseStudy.id}`} className="btn btn-link text-primary p-0">
                              Read Case Study <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                            </Link>
                          </div>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-light-custom">
        <Container>
          <h2 className="section-title">Client Testimonials</h2>
          <Row className="justify-content-center">
            <Col lg={10}>
              <Row>
                <Col md={6} className="mb-4">
                  <Card className="card-custom h-100">
                    <Card.Body>
                      <div className="d-flex mb-4">
                        <div className="me-4">
                          <img 
                            src="/images/client-1.jpg" 
                            alt="Client" 
                            className="rounded-circle"
                            style={{ width: '70px', height: '70px', objectFit: 'cover' }}
                          />
                        </div>
                        <div>
                          <h5 className="mb-1">Robert Johnson</h5>
                          <p className="text-muted mb-0">CTO, EnergySolutions Corp</p>
                        </div>
                      </div>
                      <p className="fst-italic">
                        "TechInnovate transformed our outdated systems into a state-of-the-art energy management platform. 
                        Their technical expertise and industry knowledge were impressive, and they delivered on time and 
                        within budget. The results have exceeded our expectations, with a 30% reduction in operational costs."
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={6} className="mb-4">
                  <Card className="card-custom h-100">
                    <Card.Body>
                      <div className="d-flex mb-4">
                        <div className="me-4">
                          <img 
                            src="/images/client-2.jpg" 
                            alt="Client" 
                            className="rounded-circle"
                            style={{ width: '70px', height: '70px', objectFit: 'cover' }}
                          />
                        </div>
                        <div>
                          <h5 className="mb-1">Maria Sanchez</h5>
                          <p className="text-muted mb-0">Director of Innovation, Global Manufacturing Inc.</p>
                        </div>
                      </div>
                      <p className="fst-italic">
                        "Implementing blockchain technology for our supply chain seemed daunting, but TechInnovate made 
                        the process seamless. Their team's expertise in emerging technologies and ability to translate 
                        complex concepts into practical solutions was invaluable. We now have complete visibility and 
                        trust in our supply chain."
                      </p>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Call to Action */}
      <section className="py-5">
        <Container className="text-center">
          <h2 className="mb-4">Ready to Create Your Success Story?</h2>
          <p className="lead mb-4">
            Let's discuss how our technology solutions can help you overcome challenges and achieve your business goals.
          </p>
          <Button as={Link} to="/contact" variant="primary" size="lg" className="px-4 py-2">
            Get in Touch Today
          </Button>
        </Container>
      </section>

      {/* Related Resources */}
      <section className="section-padding bg-light-custom">
        <Container>
          <h2 className="section-title mb-4">Related Resources</h2>
          <Row>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h3 className="h5 mb-3">White Papers</h3>
                  <p>Access our industry research and technological insights to help guide your strategic decisions.</p>
                  <Link to="/resources/white-papers" className="btn btn-link text-primary p-0">
                    Browse White Papers <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                  </Link>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h3 className="h5 mb-3">Webinars</h3>
                  <p>Watch on-demand presentations from our technical experts on emerging technologies and industry trends.</p>
                  <Link to="/resources/webinars" className="btn btn-link text-primary p-0">
                    View Webinars <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                  </Link>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="mb-4">
              <Card className="h-100 border-0 shadow-sm">
                <Card.Body>
                  <h3 className="h5 mb-3">Blog</h3>
                  <p>Stay informed with the latest insights, technology updates, and company news from our tech experts.</p>
                  <Link to="/blog" className="btn btn-link text-primary p-0">
                    Read Our Blog <FontAwesomeIcon icon={faArrowRight} className="ms-1" />
                  </Link>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <section className="section-padding">
        <Container>
          <Row className="justify-content-center">
            <Col lg={8} className="text-center mb-5">
              <h2 className="section-title">Frequently Asked Questions</h2>
              <p className="lead">Common questions about our case studies and project implementation process</p>
            </Col>
          </Row>
          
          <Row className="justify-content-center">
            <Col lg={10}>
              <div className="accordion" id="faqAccordion">
                <div className="accordion-item mb-3 border">
                  <h3 className="accordion-header" id="headingOne">
                    <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded="true" aria-controls="collapseOne">
                      How do you approach new client projects?
                    </button>
                  </h3>
                  <div id="collapseOne" className="accordion-collapse collapse show" aria-labelledby="headingOne" data-bs-parent="#faqAccordion">
                    <div className="accordion-body">
                      We begin with a thorough discovery phase to understand your business needs, challenges, and goals. Our team then develops a tailored solution strategy, followed by an agile implementation process with regular checkpoints to ensure alignment and satisfaction throughout the project lifecycle.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item mb-3 border">
                  <h3 className="accordion-header" id="headingTwo">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseTwo" aria-expanded="false" aria-controls="collapseTwo">
                      What industries do you specialize in?
                    </button>
                  </h3>
                  <div id="collapseTwo" className="accordion-collapse collapse" aria-labelledby="headingTwo" data-bs-parent="#faqAccordion">
                    <div className="accordion-body">
                      We have expertise across multiple industries including healthcare, finance, manufacturing, energy, retail, and logistics. Our team includes specialists with industry-specific knowledge who understand the unique challenges and regulations in these sectors.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item mb-3 border">
                  <h3 className="accordion-header" id="headingThree">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseThree" aria-expanded="false" aria-controls="collapseThree">
                      How do you measure project success?
                    </button>
                  </h3>
                  <div id="collapseThree" className="accordion-collapse collapse" aria-labelledby="headingThree" data-bs-parent="#faqAccordion">
                    <div className="accordion-body">
                      We establish clear key performance indicators (KPIs) at the beginning of each project based on your business objectives. These might include metrics like cost reduction, revenue growth, efficiency improvements, user adoption rates, or other relevant measures of success for your specific goals.
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item border">
                  <h3 className="accordion-header" id="headingFour">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#collapseFour" aria-expanded="false" aria-controls="collapseFour">
                      Do you offer ongoing support after project completion?
                    </button>
                  </h3>
                  <div id="collapseFour" className="accordion-collapse collapse" aria-labelledby="headingFour" data-bs-parent="#faqAccordion">
                    <div className="accordion-body">
                      Yes, we offer comprehensive support and maintenance packages tailored to your needs. These include technical support, system monitoring, regular updates, performance optimization, and training for your team to ensure long-term success of your solution.
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default CaseStudiesPage;